import { memo, ReactElement, useCallback, useEffect, useRef } from 'react';
import classnames from 'classnames';

import { WithError } from '@eva/types/dto';

import type {
  GroupedTableClassNames,
  RearrangeHandler,
  WithMeta,
  RowState,
  ControlDescriptor,
  GetFormFunction,
} from '../types';

import Controls from '../Controls';
import { useDndItem } from '../hooks';

import './Item.scss';

export interface GroupControlsState<I> {
  currentlyEditedItem?: I;
  currentlyDeletedItem?: I;
}

export interface ItemProps<I extends WithError, G> {
  item: WithMeta<I>;
  groups: G[];
  groupId: number;
  itemState: RowState;
  itemOrder: number;
  isSelected?: boolean;
  dndEnabled?: boolean;
  classNames: GroupedTableClassNames;
  controls?: ControlDescriptor<I, G>[];
  getItemId: (item: I) => number;
  getItemForm: GetFormFunction<I, RowState>;
  getItemLabel: (item: I) => ReactElement | string;
  getItemError?: (error: string, item: I) => ReactElement | string;
  isItemSortable?: (item: I) => boolean;
  onRearrange?: RearrangeHandler;
  updateItemState: (item: I, updates: Partial<RowState>) => void;
}

const Item = <I extends WithError, G extends WithError>({
  item,
  groups,
  groupId,
  controls,
  isSelected,
  dndEnabled,
  itemState,
  itemOrder,
  classNames,
  getItemId,
  getItemForm,
  getItemLabel,
  getItemError,
  isItemSortable,
  onRearrange,
  updateItemState,
}: ItemProps<I, G>) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const itemId = getItemId(item);

  const isSortable = typeof isItemSortable === 'function' ? isItemSortable(item) : false;

  const isDraggable = !isSortable && dndEnabled && !itemState?.error;

  const { drag, drop, isDragging, draggingItem, handlerId } = useDndItem({
    itemId,
    groupId,
    payload: item,
    index: itemOrder,
    ref: containerRef,
    disabled: !isDraggable || itemState?.isConfirming,
    onRearrange,
  });

  const onUpdateRowState = useCallback(
    (updates: Partial<RowState>) => {
      if (typeof updateItemState === 'function') {
        updateItemState(item, updates);
      }
    },
    [item, updateItemState],
  );

  useEffect(() => {
    onUpdateRowState({ isDragging });
  }, [isDragging, onUpdateRowState]);

  const onShowItemForm = useCallback(() => {
    if (!itemId) {
      return;
    }
    onUpdateRowState({
      inlineForm:
        typeof getItemForm === 'function'
          ? getItemForm(
              item,
              {
                updateRowState: onUpdateRowState,
              },
              itemState,
            )
          : null,
    });
  }, [item, itemId, itemState, getItemForm, onUpdateRowState]);

  if (itemState == null) {
    return null;
  }

  const errorElement =
    itemState.error && typeof getItemError === 'function' ? (
      getItemError(itemState.error, item)
    ) : (
      <div className="margin-left text-danger padding-top">{itemState.error}</div>
    );

  const messageElement = !!itemState.message && <div className="text-danger small">{itemState.message}</div>;

  let formOrTitle = null;

  if (itemState.inlineForm) {
    formOrTitle = (
      <div className={classNames.itemInlineFormClassName}>
        <div>{itemState.inlineForm}</div>
        {errorElement}
      </div>
    );
  } else {
    const labelRole = typeof getItemForm === 'function' ? 'button' : 'presentation';

    formOrTitle = (
      <span role={labelRole} onClick={onShowItemForm}>
        {getItemLabel(item)}
      </span>
    );
  }

  const itemClassNames = classnames({
    [classNames.itemClassName]: true,
    [classNames.itemIsSelectedClassName]: isSelected,
    [classNames.itemIsConfirmingClassName]: itemState?.isConfirming,
    [classNames.itemIsDraggingClassName]: isDragging || draggingItem?.itemId === itemId,
  });

  const itemContentClassNames = classnames({
    [classNames.itemContentClassName]: true,
  });

  const itemControlsClassNames = classnames([classNames.itemControlsClassName]);

  const itemTitleClassNames = classnames([classNames.itemTitleClassName]);

  const dndIconStyle = isDraggable ? { color: draggingItem?.itemId === itemId ? '#1088E2' : '#96ACBF' } : {};

  if (isDraggable) {
    drag(drop(containerRef));
  }

  return (
    <div ref={containerRef} className={itemClassNames} data-handler-id={handlerId}>
      {controls?.length && (
        <div className={itemControlsClassNames}>
          <Controls
            entity={item}
            groups={groups}
            controls={controls}
            rowState={itemState}
            classNames={classNames}
            getEntityId={getItemId}
            updateRowState={onUpdateRowState}
          />
        </div>
      )}
      <div className={itemContentClassNames}>
        <span className={itemTitleClassNames}>
          {isDraggable && <i className="fa fa-ellipsis-v margin-right" style={dndIconStyle} />}
          {formOrTitle}
        </span>
        {errorElement}
      </div>
      {messageElement}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(Item);
